import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, CircularProgress, Typography, Toolbar } from '@mui/material';
import { ITutor } from '../../types';
import tutorService from '../../services/tutorService';
import ErrorAlert from '../../components/common/ErrorAlert';
import PublicTutorProfileCard from '../../components/tutors/PublicTutorProfileCard';
import TutorLeadNavbar from '../../components/tutors/TutorLeadNavbar';

const TutorPublicProfilePage: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const [tutor, setTutor] = useState<ITutor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTutorAndReviews = async () => {
      if (!teacherId) {
        setError('Tutor not found');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const res = await tutorService.getPublicTutorProfile(teacherId);
        setTutor(res.data);
      } catch (e: any) {
        const msg = e?.response?.data?.message || e?.message || 'Failed to load tutor profile.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchTutorAndReviews();
    
    // Set document title
    document.title = "YourShikshak Tutor Profile";
    
    return () => {
      // Optional: reset title on unmount
      document.title = "YourShikshak";
    };
  }, [teacherId]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={32} />
        </Box>
      </Container>
    );
  }

  if (error || !tutor) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #FEF2F2 0%, #FFF7ED 50%, #EFF6FF 100%)',
        }}
      >
        <Container maxWidth="sm">
          <ErrorAlert error={error || 'Tutor not found'} />
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #EEF2FF 0%, #F9FAFB 40%, #ECFDF5 100%)',
      }}
    >
      <TutorLeadNavbar />
      <Toolbar sx={{ mb: { xs: 2, sm: 4 } }} />
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
        <Typography 
          variant="h4" 
          align="center" 
          fontWeight={900} 
          gutterBottom 
          sx={{ 
            mb: 4, 
            color: '#1e293b',
            letterSpacing: '-0.02em'
          }}
        >
          Your Shikshak Tutor Profile
        </Typography>
        <PublicTutorProfileCard tutor={tutor} />
      </Container>
    </Box>
  );
}

export default TutorPublicProfilePage;

